/** biome-ignore-all lint/style/noMagicNumbers: TODO: fix */
import { useEffect, useMemo, useState } from 'react';
import { Button, Col, notification, Popconfirm, Row, Table } from 'antd';

import FormSelectBox from '@/views/components/Inputs/FormSelectBox';
import { calculateLinearFeet, getTrailerLabel } from '@/views/screens/Savers/utils';

import { ALLOWED_PRODUCT_VALUES } from '../shipment/constants';

const ProductTypes = ({
  productType,
  form,
  commodityOptions,
  tableData,
  setTableData,
  productTypeName,
  trailerSizeOptions,
  setPercentage,
  isMixed,
}) => {
  const [count, setCount] = useState(tableData.length > 0 ? tableData.length : 1);

  const handleAddRow = () => {
    const newKey = count + 1;
    const newRow = {
      estDimension: '',
      estWeight: '',
      key: newKey,
      productType: null,
    };
    setTableData((prev) => [...prev, newRow]);
    setCount(newKey);
  };

  const handleDelete = (key) => {
    if (tableData.length <= 1) {
      notification.error({
        description: 'At least one product must remain',
        message: 'Error',
      });
      return;
    }
    setTableData((prev) => prev.filter((item) => item.key !== key));
  };

  const handleProductTypeChange = (value, rowKey) => {
    const selectedOption = commodityOptions.find((opt) => opt.value === value);
    setTableData((prev) =>
      prev.map((row) => {
        if (row.key === rowKey) {
          const weight = parseFloat(row.estWeight);
          const newDimension = weight && value ? calculateLinearFeet(weight, value) : '';
          return {
            ...row,
            estDimension: newDimension,
            productType: value,
            productTypeLabel: selectedOption ? selectedOption.label : null,
          };
        }
        return row;
      }),
    );
  };

  const handleWeightChange = (e, rowKey) => {
    const value = e.target.value;
    setTableData((prev) =>
      prev.map((row) => {
        if (row.key === rowKey) {
          const weight = parseFloat(value);

          const activeProductType = isMixed ? row.productType : Number(productType);

          const newDimension = weight && activeProductType ? calculateLinearFeet(weight, activeProductType) : '';

          return {
            ...row,
            estDimension: newDimension,
            estWeight: value,
          };
        }
        return row;
      }),
    );
  };

  const selectedProductTypes = useMemo(
    () => tableData.map((item) => Number(item.productType)).filter(Boolean),
    [tableData],
  );

  const availableProductOptions = useMemo(
    () => commodityOptions.filter((opt) => ALLOWED_PRODUCT_VALUES.includes(opt.value)),
    [commodityOptions],
  );
  const allOptionsUsed = selectedProductTypes.length >= availableProductOptions.length;

  const columns = [
    {
      dataIndex: 'productType',
      key: 'productType',
      render: (value, record) =>
        isMixed ? (
          <FormSelectBox
            label=""
            editMode
            name={`productType${record.key}`}
            margin="mb-10"
            options={availableProductOptions.filter((opt) => {
              const optValue = Number(opt.value);
              const recordValue = Number(record.productType);

              return !selectedProductTypes.includes(optValue) || optValue === recordValue;
            })}
            placeholder="Product Type"
            className="P-0"
            value={value}
            onChange={(val) => handleProductTypeChange(val?.value, record.key)}
          />
        ) : (
          <input type="text" disabled value={productTypeName} />
        ),
      title: 'Product Type',
    },
    {
      dataIndex: 'estWeight',
      key: 'estWeight',
      render: (value, record) => (
        <input
          type="number"
          min={0}
          id={`txtProdWeight${record.key}`}
          name={`estWeight${record.key}`}
          className="rounded-lg w-full mb-10 p-4"
          placeholder="Estimated Weight"
          value={value}
          onChange={(e) => handleWeightChange(e, record.key)}
        />
      ),
      title: 'Estimated Weight',
    },
    {
      dataIndex: 'estDimension',
      key: 'estDimension',
      render: (value) => (
        <input disabled className="mb-10 p-4 w-full" placeholder="Estimated Linear Feet" value={value} />
      ),
      title: 'Estimated Linear Feet',
    },
    {
      hidden: !isMixed,
      key: 'action',
      render: (_, record) =>
        isMixed && (
          <Popconfirm title="Are you sure you want to delete this row?" onConfirm={() => handleDelete(record.key)}>
            <Button danger>Delete</Button>
          </Popconfirm>
        ),
      title: 'Action',
    },
  ].filter((col) => !col.hidden);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: Fix this
  useEffect(() => {
    if (!trailerSizeOptions || tableData.length === 0) {
      return;
    }

    try {
      const totalEstimatedDimension = isMixed
        ? tableData.map((item) => Number(item.estDimension) || 0).reduce((a, b) => a + b, 0)
        : tableData[0]?.estDimension || 0;

      const trailerSize = form.getFieldValue('trailerSize') || 0;
      const trailerValue = getTrailerLabel(trailerSize, trailerSizeOptions);

      const newPercentage =
        trailerValue > 0 && totalEstimatedDimension >= 0
          ? Math.min((totalEstimatedDimension / trailerValue) * 100, 100)
          : 0;
      setPercentage(newPercentage);
      form?.setFieldValue(
        'productTypeInput',
        tableData
          ?.map((res) => res.productTypeLabel?.trim())
          .filter(Boolean)
          .join(', '),
      );
    } catch (err) {
      console.error('Error calculating trailer percentage:', err);
    }
  }, [tableData, trailerSizeOptions]);

  return (
    <Row>
      <Col span={24}>
        {isMixed && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 16,
              marginTop: 16,
            }}
          >
            <Button type="primary" onClick={handleAddRow} disabled={allOptionsUsed}>
              Add Product
            </Button>
          </div>
        )}
        <Table
          dataSource={tableData}
          columns={columns}
          scroll={isMixed ? { x: 'max-content', y: 300 } : null}
          pagination={false}
          rowKey="key"
        />
      </Col>
    </Row>
  );
};

export default ProductTypes;
